/*
 * IconListGrid.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { IIconListPack } from "./IconListTypes";
import { IconListPreview } from "./IconListPreview";
import { useIconListContext } from "./IconListContext";
import { VirtualGrid } from "../VirtualGrid";
import { useDeepCompareMemo } from "use-deep-compare";

export interface IIconListGridProps {
	className?: string;
	iconPacks: IIconListPack[];
}

const List = React.forwardRef<HTMLDivElement, { className?: string }>(({ className, ...props }, ref) => {
	const { iconPreviewSize } = useIconListContext();
	const baseClass = BEM("edt-icon-list-grid", null, { "icon-size": iconPreviewSize });

	if (className) {
		baseClass.add(className);
	}

	return <div className={baseClass.toClassName()} {...props} ref={ref} />;
});

export const IconListGrid: React.FC<IIconListGridProps> = ({ className, iconPacks }) => {
	const { selectedIconName, selectedPackName } = useIconListContext();

	const mergedItems = useDeepCompareMemo(
		() =>
			iconPacks.reduce((acc, iconPack) => {
				const iconItems = iconPack.icons.map((icon) => ({ ...icon, packName: iconPack.name }));
				return [ ...acc, ...iconItems ];
			}, []),
		[ iconPacks ]
	);

	return (
		<VirtualGrid
			className={className}
			totalCount={mergedItems.length}
			overscan={200}
			components={{
				List: List
			}}
			itemContent={(index) => {
				const iconData = mergedItems[index];

				return (
					<IconListPreview
						iconName={iconData.name}
						packName={iconData.packName}
						title={iconData.title}
						active={selectedPackName === iconData.packName && selectedIconName === iconData.name}
					/>
				);
			}}
		/>
	);
};
